"use client";

import { useState, useEffect } from "react";
import { apiGet, apiPut } from "@/integrations/api/client";

export interface AvailabilitySettings {
  status: "available" | "busy";
  slots: number;
}

const DEFAULT_AVAILABILITY: AvailabilitySettings = {
  status: "available",
  slots: 4,
};

let cachedAvailability: AvailabilitySettings = { ...DEFAULT_AVAILABILITY };
let hasResolvedAvailability = false;
let inflightAvailabilityRequest: Promise<AvailabilitySettings> | null = null;

const AVAILABILITY_STORAGE_KEY = "availability";

function readStoredAvailability(): AvailabilitySettings {
  if (typeof window === "undefined") return { ...DEFAULT_AVAILABILITY };
  try {
    const raw = window.localStorage.getItem(AVAILABILITY_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_AVAILABILITY };
    const parsed = JSON.parse(raw) as Partial<AvailabilitySettings>;
    return {
      status: parsed.status === "busy" ? "busy" : "available",
      slots: typeof parsed.slots === "number" ? parsed.slots : DEFAULT_AVAILABILITY.slots,
    };
  } catch {
    return { ...DEFAULT_AVAILABILITY };
  }
}

function storeAvailability(value: AvailabilitySettings) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AVAILABILITY_STORAGE_KEY, JSON.stringify(value));
}

export function useAvailability(options?: { enabled?: boolean; defer?: boolean }) {
  // Always start with DEFAULT_AVAILABILITY for server-client parity.
  // localStorage value resolved in mount effect (avoids hydration mismatch).
  const [availability, setAvailability] = useState<AvailabilitySettings>(() => ({
    ...DEFAULT_AVAILABILITY,
  }));
  const [loading, setLoading] = useState(!hasResolvedAvailability);
  const isEnabled = options?.enabled ?? true;
  const shouldDefer = options?.defer ?? false;

  const fetchAvailability = async () => {
    if (hasResolvedAvailability) {
      setAvailability(cachedAvailability);
      setLoading(false);
      return cachedAvailability;
    }

    if (!inflightAvailabilityRequest) {
      inflightAvailabilityRequest = (async () => {
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const data = await apiGet<{ value: AvailabilitySettings }>(
              "/settings/availability"
            );
            if (data?.value) {
              cachedAvailability = data.value;
              storeAvailability(data.value);
            }
            hasResolvedAvailability = true;
            return cachedAvailability;
          } catch {
            if (attempt < 2) {
              await new Promise<void>((r) => setTimeout(r, 2000 * (attempt + 1)));
            }
          }
        }
        return cachedAvailability;
      })().finally(() => {
        inflightAvailabilityRequest = null;
      });
    }

    setLoading(true);
    const nextAvailability = await inflightAvailabilityRequest;
    setAvailability(nextAvailability);
    setLoading(false);
    return nextAvailability;
  };

  const updateAvailability = async (newSettings: AvailabilitySettings) => {
    try {
      await apiPut("/admin/settings", {
        key: "availability",
        value: newSettings,
      });
      setAvailability(newSettings);
      cachedAvailability = newSettings;
      hasResolvedAvailability = true;
      storeAvailability(newSettings);
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  useEffect(() => {
    if (!isEnabled) {
      setLoading(false);
      return;
    }

    if (!shouldDefer || hasResolvedAvailability) {
      void fetchAvailability();
      return;
    }

    let cancelled = false;
    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const runFetch = () => {
      if (!cancelled) {
        void fetchAvailability();
      }
    };

    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(runFetch, { timeout: 1800 });
    } else {
      timeoutId = setTimeout(runFetch, 900) as unknown as number;
    }

    return () => {
      cancelled = true;
      if (idleId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isEnabled, shouldDefer]);

  return { availability, loading, updateAvailability, refetch: fetchAvailability };
}
